import Input from "@/v2-ui/input"
import Button from "@/v2-backoffice-client/components/button"
import TranslationEditorLanguageAiStatus from "@/v2-backoffice-client/translation/editor/TranslationEditorLanguageAiStatus"
import {
  isDirty,
  type LangEdit,
} from "@/v2-backoffice-client/translation/editor/translation.editor.utils"
import type { SupportedLanguage } from "@/v2-backoffice-client/translation/translation.types"

export type TranslationEditorLanguageProps = {
  lang: SupportedLanguage
  edit: LangEdit
  // This language was the anchor of the last suggestion run.
  isSource: boolean
  // A suggest run has completed for this row (drives the AI feedback line).
  hasGenerated: boolean
  aiLoading: boolean
  isSaving: boolean
  // Extra external reason this row's own actions should be inert (e.g. the
  // row is being deleted, or a sibling create is in flight) — ANDed into
  // Save/Translate from's own disabled logic. Optional and false by default
  // so existing callers are unaffected.
  disabled?: boolean
  onPatch(next: Partial<LangEdit>): void
  onApplySuggestion(): void
  onSave(): void
  onSuggestFrom(): void
}

// One language row inside the inline editor: label, editable Name +
// Description (default content-sized, horizontally resizable via their
// wrappers), then the row's actions — all anchored left. The AI feedback
// line below the inputs is a fixed-height slot so state changes never shift
// the layout.
function TranslationEditorLanguage(props: TranslationEditorLanguageProps) {
  const {
    lang,
    edit,
    isSource,
    hasGenerated,
    aiLoading,
    isSaving,
    disabled = false,
    onPatch,
    onApplySuggestion,
    onSave,
  } = props

  const missing = !edit.baseName
  const dirty = isDirty(edit)

  return (
    <div className="translation-editor-language">
      <div className="translation-editor-language-main">
        <div className="translation-editor-language-label">
          <span
            className="truncate text-xs font-medium text-foreground"
            title={`${lang.label} (${lang.code})${missing ? " — no text stored yet" : ""}`}
          >
            {lang.label}
          </span>
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            {lang.code}
            {missing ? (
              <span
                className="rounded bg-destructive/15 px-1 text-[10px] font-medium text-destructive"
                title="No text stored for this language yet — it falls back to English in the app."
              >
                missing
              </span>
            ) : null}
            {isSource ? (
              <span
                className="rounded bg-primary/15 px-1 text-[10px] text-primary"
                title="The last AI suggestion run used this language's text as the source of truth."
              >
                source
              </span>
            ) : null}
          </span>
        </div>

        <div className="translation-editor-field translation-editor-field-name">
          <Input
            className="w-full"
            value={edit.curName}
            placeholder="Name"
            onChange={(e) => onPatch({ curName: e.target.value })}
            data-feature-code="UI_BackofficeTranslation_Editor_CurrentName"
            title="Name stored in common.Translations for this language. Editable; Save writes exactly what is here. Drag the corner to resize."
          />
        </div>

        <div className="translation-editor-field translation-editor-field-desc">
          <textarea
            className="translation-editor-language-textarea w-full"
            value={edit.curDesc}
            rows={1}
            placeholder="Description"
            onChange={(e) => onPatch({ curDesc: e.target.value })}
            title="Description stored in common.Translations for this language. Drag the corner to resize."
          />
        </div>

        <span className="flex items-center gap-1.5">
          <Button
            size="tiny"
            variant={dirty ? "primary" : "secondary"}
            data-feature-code="UI_BackofficeTranslation_Editor_Save"
            onClick={onSave}
            disabled={!dirty || isSaving || disabled}
            isLoading={isSaving}
            title="Write this language's current text to common.Translations via api.Upsert_Common_Translation (@autoTranslate=0, so the DB-side LLM is not re-run)."
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
          <Button
            size="tiny"
            variant="secondary"
            data-feature-code="UI_BackofficeTranslation_Editor_SuggestFrom"
            onClick={props.onSuggestFrom}
            disabled={aiLoading || (!edit.curName && !edit.curDesc) || disabled}
            isLoading={aiLoading && isSource}
            title={`Generate AI suggestions for every other language using this ${lang.label} text as the source of truth. Anchoring on one language removes room for interpretation drift. Spends tokens; runs only on demand.`}
          >
            {aiLoading && isSource ? "Translating…" : "Translate from"}
          </Button>
        </span>
      </div>

      <span className="translation-editor-language-ai">
        <TranslationEditorLanguageAiStatus
          edit={edit}
          hasGenerated={hasGenerated}
          aiLoading={aiLoading}
          onApplySuggestion={onApplySuggestion}
        />
      </span>
    </div>
  )
}

export default TranslationEditorLanguage
